/**
 * The store the v0.7 screens read through, and the hooks that read it.
 *
 * Deliberately free of any seed import. The screens are the PRODUCT's screens, so anything they
 * import reaches the production bundle; the demo's 450 KB of synthetic records must not, both
 * because it is a large download and because a fixture credential has no business appearing, even
 * for a frame, in front of a real user. The demo's own provider lives next door in `provider.tsx`
 * and the product's in `api-provider.tsx`; both fill the same shape.
 */
import { createContext, type ReactElement, type ReactNode, useContext } from 'react';
import type {
  DemoBatch,
  DemoCredential,
  DemoDatabase,
  DemoDelivery,
  DemoEvent,
  DemoSettings,
  DemoSubject,
  DemoTemplate,
} from './types';

export interface DemoStore {
  db: DemoDatabase;
  /** Applies a change to the database in memory and re-renders every screen reading from it. */
  update: (mutate: (db: DemoDatabase) => void) => void;
  /** Returns the session to the prototype's own seed. Demo only; a no-op in the product. */
  reset: () => void;
}

const StoreContext = createContext<DemoStore | null>(null);

/**
 * The store, whoever filled it.
 *
 * A screen cannot tell which provider is above it — which is the whole reason the pixel acceptance
 * measures the components the product renders.
 */
export function V07StoreProvider({
  store,
  children,
}: {
  store: DemoStore;
  children: ReactNode;
}): ReactElement {
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}

function useStore(): DemoStore {
  const store = useContext(StoreContext);
  if (store === null) throw new Error('useDemoData must be used inside a v0.7 store provider');
  return store;
}

export function useDemoData(): DemoDatabase {
  return useStore().db;
}

export function useDemoUpdate(): (mutate: (db: DemoDatabase) => void) => void {
  return useStore().update;
}

export function useDemoReset(): () => void {
  return useStore().reset;
}

// ---------------------------------------------------------------- reads the screens actually use

export function useTemplates(): DemoTemplate[] {
  return useDemoData().templates;
}

export function useTemplate(id: string | undefined): DemoTemplate | undefined {
  const templates = useTemplates();
  return templates.find((t) => t.id === id || t.code === id);
}

export function useCredentials(): DemoCredential[] {
  return useDemoData().credentials;
}

export function useCredential(code: string | undefined): DemoCredential | undefined {
  const credentials = useCredentials();
  if (!code) return undefined;
  const wanted = code.toUpperCase();
  return credentials.find((c) => c.publicCode.toUpperCase() === wanted || c.id === code);
}

export function useBatches(): DemoBatch[] {
  return useDemoData().batches;
}

export function useBatch(id: string | undefined): DemoBatch | undefined {
  const batches = useBatches();
  return batches.find((b) => b.id === id);
}

export function useSubjects(): DemoSubject[] {
  return useDemoData().subjects;
}

export function useSubject(id: string | undefined): DemoSubject | undefined {
  return useSubjects().find((s) => s.id === id);
}

export function useEvents(): DemoEvent[] {
  return useDemoData().eventLog;
}

export function useDeliveries(): DemoDelivery[] {
  return useDemoData().deliveries;
}

export function useSettings(): DemoSettings {
  return useDemoData().settings;
}
